import React, { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { STORAGE_KEYS, readStorageValue, removeStorageValue, writeStorageValue } from '../utils/storageKeys';

const AuthContext = createContext(undefined);

// Mock users database
const MOCK_USERS = [
  {
    email: 'superadmin@agrilink.dev',
    password: 'superadmin123',
    role: 'superadmin',
    firstName: 'Super',
    lastName: 'Admin',
  },
  {
    email: 'admin@agrilink.dev',
    password: 'admin123',
    role: 'admin',
    firstName: 'Admin',
    lastName: 'User',
  },
  {
    email: 'reviewer@agrilink.dev',
    password: 'reviewer123',
    role: 'reviewer',
    firstName: 'John',
    lastName: 'Reviewer',
  },
  {
    email: 'viewer@agrilink.dev',
    password: 'viewer123',
    role: 'viewer',
    firstName: 'Maria',
    lastName: 'Viewer',
  },
];

// Mock login function
const mockLogin = async (email, password, rememberMe) => {
  await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API

  const account = MOCK_USERS.find(u => u.email === email);

  if (account && password === account.password) {
    const user = {
      id: Math.random().toString(36).substr(2, 9),
      email: account.email,
      firstName: account.firstName,
      lastName: account.lastName,
      role: account.role,
    };

    const token = 'mock-jwt-token-' + Date.now();
    const expiresAt = Date.now() + (rememberMe ? 30 * 24 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000);

    return {
      success: true,
      user,
      token,
      expiresAt,
    };
  }

  return {
    success: false,
    error: 'Invalid email or password',
  };
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load session from localStorage on mount
  useEffect(() => {
    const sessionEntry = readStorageValue(window.localStorage, STORAGE_KEYS.session);
    if (sessionEntry?.value) {
      try {
        const session = JSON.parse(sessionEntry.value);

        // Check if session is still valid
        if (session.expiresAt > Date.now()) {
          setUser(session.user);
          writeStorageValue(window.localStorage, STORAGE_KEYS.session, JSON.stringify(session));
        } else {
          // Session expired, clear it
          removeStorageValue(window.localStorage, STORAGE_KEYS.session);
        }
      } catch (e) {
        console.error('Failed to load session:', e);
        removeStorageValue(window.localStorage, STORAGE_KEYS.session);
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password, rememberMe = false) => {
    const result = await mockLogin(email, password, rememberMe);

    if (result.success) {
      const session = {
        token: result.token,
        user: result.user,
        expiresAt: result.expiresAt,
        rememberMe,
      };

      writeStorageValue(window.localStorage, STORAGE_KEYS.session, JSON.stringify(session));
      setUser(result.user);

      return { success: true, user: result.user };
    }

    return { success: false, error: result.error };
  };

  const logout = () => {
    removeStorageValue(window.localStorage, STORAGE_KEYS.session);
    if (typeof window !== 'undefined' && window.sessionStorage) {
      removeStorageValue(window.sessionStorage, STORAGE_KEYS.analyticsSelection);
    }
    setUser(null);
  };

  const isAuthenticated = () => {
    return user !== null;
  };

  const switchRole = (newRole) => {
    if (!user) return;

    const updatedUser = { ...user, role: newRole };
    setUser(updatedUser);

    // Update local storage
    const sessionEntry = readStorageValue(window.localStorage, STORAGE_KEYS.session);
    if (sessionEntry?.value) {
      const session = JSON.parse(sessionEntry.value);
      session.user = updatedUser;
      writeStorageValue(window.localStorage, STORAGE_KEYS.session, JSON.stringify(session));
    }
  };

  const value = {
    user,
    login,
    logout,
    switchRole,
    isAuthenticated,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
